import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import CompleteComplianceDialog from '../components/CompleteComplianceDialog';
import ComplianceItemForm from '../components/ComplianceItemForm';
import {
  MapPin,
  FileText,
  ShieldCheck,
  FolderOpen,
  ChevronRight,
  AlertTriangle,
  Clock,
  ArrowLeft,
  Download,
  ExternalLink,
  Activity,
  User,
  Loader2,
  Pencil,
  TrendingUp,
  TrendingDown,
  Minus,
  Check,
  Upload,
} from 'lucide-react';

const CATEGORY_LABELS = {
  financieel: 'Financieel & Belasting',
  contracten: 'Contracten & Huur',
  leveranciers: 'Leveranciers & Inkoop',
  techniek: 'Techniek & Apparatuur',
  voertuigen: 'Voertuigen',
  vastgoed: 'Vastgoed & Facilitair',
  veiligheid: 'Veiligheid & Compliance',
  milieu: 'Milieu & Infrastructuur',
  nieuwbouw: 'Nieuwbouw & Verbouwing',
  overnames: 'Overnames',
  automatisering: 'Automatisering',
};

const TYPE_CONFIG = {
  keuring: { label: 'Keuring', color: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-500/10 dark:text-purple-400 dark:border-purple-500/30' },
  contract: { label: 'Contract', color: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/30' },
  onderhoud: { label: 'Onderhoud', color: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/30' },
  voertuig: { label: 'Voertuig', color: 'bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-500/10 dark:text-teal-400 dark:border-teal-500/30' },
  vergunning: { label: 'Vergunning', color: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/10 dark:text-rose-400 dark:border-rose-500/30' },
  betaling: { label: 'Betaling', color: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/30' },
};

const STATUS_CONFIG = {
  overdue: { label: 'Te laat', badgeClass: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/30' },
  due_soon: { label: 'Binnenkort', badgeClass: 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-500/10 dark:text-orange-400 dark:border-orange-500/30' },
  upcoming: { label: 'Aankomend', badgeClass: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/30' },
  completed: { label: 'Afgerond', badgeClass: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-500/10 dark:text-green-400 dark:border-green-500/30' },
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  try { return new Date(dateStr).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return dateStr; }
}

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function getFileIcon(mimeType) {
  if (!mimeType) return 'file';
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.includes('pdf')) return 'pdf';
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return 'spreadsheet';
  if (mimeType.includes('word') || mimeType.includes('document')) return 'document';
  return 'file';
}

export default function LocationPage() {
  const { locationId } = useParams();
  const { authAxios, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overzicht');
  const [entries, setEntries] = useState([]);
  const [complianceItems, setComplianceItems] = useState([]);
  const [files, setFiles] = useState([]);
  const [entriesLoaded, setEntriesLoaded] = useState(false);
  const [complianceLoaded, setComplianceLoaded] = useState(false);
  const [filesLoaded, setFilesLoaded] = useState(false);
  const [quickCompleteItem, setQuickCompleteItem] = useState(null);
  const [showComplianceForm, setShowComplianceForm] = useState(false);
  const [allLocations, setAllLocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [locationScore, setLocationScore] = useState(null);
  const [exportJob, setExportJob] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [locSelectedIds, setLocSelectedIds] = useState(new Set());
  const [locBulkDialogOpen, setLocBulkDialogOpen] = useState(false);
  const [locBulkNotes, setLocBulkNotes] = useState('');
  const [locBulkCompleting, setLocBulkCompleting] = useState(false);
  const isOwner = user?.role === 'owner';

  const fetchSummary = useCallback(async () => {
    try {
      const api = authAxios();
      const res = await api.get(`/locations/${locationId}/summary`);
      setSummary(res.data);
      // Fetch score
      try {
        const scoreRes = await api.get(`/compliance-scores/${locationId}`);
        setLocationScore(scoreRes.data);
      } catch (e) { /* score optional */ }
    } catch (e) {
      toast.error('Locatie niet gevonden');
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [authAxios, locationId, navigate]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const startLocationExport = async () => {
    setExporting(true);
    try {
      const api = authAxios();
      const res = await api.post(`/export/location/${locationId}`);
      const jobId = res.data.job_id;
      const pollJob = async () => {
        try {
          const statusRes = await api.get(`/export/status/${jobId}`);
          setExportJob(statusRes.data);
          if (statusRes.data.status === 'processing') {
            setTimeout(pollJob, 2000);
          } else {
            setExporting(false);
            if (statusRes.data.status === 'done') {
              toast.success('Export is gereed!');
              api.get(`/export/download/${jobId}`, { responseType: 'blob' })
                .then(res => {
                  const blobUrl = URL.createObjectURL(res.data);
                  const a = document.createElement('a');
                  a.href = blobUrl;
                  a.download = `tw1-${locationId}-export.zip`;
                  a.click();
                  URL.revokeObjectURL(blobUrl);
                })
                .catch(() => { toast.error('Download mislukt'); });
            } else {
              toast.error('Export mislukt');
            }
          }
        } catch (e) { setExporting(false); }
      };
      pollJob();
    } catch (e) {
      toast.error('Export starten mislukt');
      setExporting(false);
    }
  };

  const handleLocBulkComplete = async () => {
    setLocBulkCompleting(true);
    try {
      const api = authAxios();
      const res = await api.post('/compliance-items/bulk-complete', {
        item_ids: Array.from(locSelectedIds),
        notes: locBulkNotes,
      });
      toast.success(`${res.data.completed} items afgerond`);
      setLocBulkDialogOpen(false);
      setLocBulkNotes('');
      setLocSelectedIds(new Set());
      setComplianceLoaded(false);
      loadCompliance();
      fetchSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fout bij bulk afronden');
    } finally {
      setLocBulkCompleting(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const api = authAxios();
        const [locRes, usersRes] = await Promise.all([api.get('/locations'), api.get('/users/list')]);
        setAllLocations(locRes.data);
        setUsers(usersRes.data);
      } catch (e) { /* silent */ }
    };
    load();
  }, [authAxios]);

  const loadEntries = useCallback(async () => {
    if (entriesLoaded) return;
    try {
      const api = authAxios();
      const res = await api.get(`/entries?location_id=${locationId}&limit=100`);
      setEntries(res.data.entries || []);
    } catch (e) { console.error(e); }
    setEntriesLoaded(true);
  }, [authAxios, locationId, entriesLoaded]);

  const loadCompliance = useCallback(async () => {
    if (complianceLoaded) return;
    try {
      const api = authAxios();
      const res = await api.get(`/compliance-items?location_id=${locationId}&include_completed=true&limit=200`);
      setComplianceItems(res.data.items || []);
    } catch (e) { console.error(e); }
    setComplianceLoaded(true);
  }, [authAxios, locationId, complianceLoaded]);

  const loadFiles = useCallback(async () => {
    if (filesLoaded) return;
    try {
      const api = authAxios();
      const res = await api.get(`/files?location_id=${locationId}&limit=100`);
      setFiles(res.data.files || []);
    } catch (e) { console.error(e); }
    setFilesLoaded(true);
  }, [authAxios, locationId, filesLoaded]);

  useEffect(() => {
    if (activeTab === 'kennisbank') loadEntries();
    if (activeTab === 'compliance') loadCompliance();
    if (activeTab === 'bestanden') loadFiles();
  }, [activeTab, loadEntries, loadCompliance, loadFiles]);

  if (loading) {
    return (
      <div className="space-y-6 max-w-[1200px]">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!summary) return null;
  const loc = summary.location;

  return (
    <div className="space-y-6 max-w-[1200px]" data-testid="location-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <button
          onClick={() => navigate('/')}
          className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-[#1C2128] transition-colors self-start"
          data-testid="location-back-button"
        >
          <ArrowLeft className="w-4 h-4 text-slate-500" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-lg sm:text-xl font-heading font-bold text-slate-900 dark:text-[#E6EDF3] truncate" data-testid="location-title">
              {loc.name}
            </h1>
            {isOwner && (
              <button
                onClick={() => setShowEditDialog(true)}
                className="p-1.5 rounded-md text-slate-400 hover:text-[#2E75B6] hover:bg-[#2E75B6]/5 transition-colors"
                data-testid="location-edit-button"
                title="Locatie bewerken"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
            <button
              onClick={startLocationExport}
              disabled={exporting}
              className="p-1.5 rounded-md text-slate-400 hover:text-[#2E75B6] hover:bg-[#2E75B6]/5 transition-colors disabled:opacity-50"
              data-testid="location-export-button"
              title="Exporteer locatie"
            >
              {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            </button>
          </div>
          <div className="flex items-center gap-3 mt-1">
            <span className="text-xs text-slate-500 dark:text-[#8B949E] capitalize">{loc.bv_id}</span>
            {loc.bays && <span className="text-xs text-slate-400 dark:text-[#484F58]">{loc.bays} bays</span>}
            <Badge variant="outline" className={`text-[10px] ${loc.status === 'active' ? 'badge-active' : 'badge-planned'}`}>
              {loc.status === 'active' ? 'Actief' : 'Gepland'}
            </Badge>
          </div>
        </div>
        {/* Compliance Score Badge */}
        {locationScore?.score != null && (
          <div
            className={`flex flex-col items-center justify-center px-4 py-2 rounded-xl border ${
              locationScore.score >= 90 ? 'bg-green-50 border-green-200 dark:bg-green-500/10 dark:border-green-500/30' :
              locationScore.score >= 70 ? 'bg-blue-50 border-blue-200 dark:bg-blue-500/10 dark:border-blue-500/30' :
              locationScore.score >= 50 ? 'bg-orange-50 border-orange-200 dark:bg-orange-500/10 dark:border-orange-500/30' :
              'bg-red-50 border-red-200 dark:bg-red-500/10 dark:border-red-500/30'
            }`}
            data-testid="location-score-display"
          >
            <div className="flex items-center gap-1.5">
              <span className={`text-2xl font-heading font-bold tabular-nums ${
                locationScore.score >= 90 ? 'text-green-700 dark:text-green-400' :
                locationScore.score >= 70 ? 'text-[#2E75B6] dark:text-blue-400' :
                locationScore.score >= 50 ? 'text-orange-700 dark:text-orange-400' :
                'text-red-700 dark:text-red-400'
              }`}>
                {Math.round(locationScore.score)}
              </span>
              {locationScore.trend === 'up' && <TrendingUp className="w-4 h-4 text-green-600" />}
              {locationScore.trend === 'down' && <TrendingDown className="w-4 h-4 text-red-500" />}
              {locationScore.trend === 'stable' && <Minus className="w-4 h-4 text-slate-400" />}
            </div>
            <span className={`text-[10px] font-medium ${
              locationScore.score >= 90 ? 'text-green-600 dark:text-green-400' :
              locationScore.score >= 70 ? 'text-[#2E75B6] dark:text-blue-400' :
              locationScore.score >= 50 ? 'text-orange-600 dark:text-orange-400' :
              'text-red-600 dark:text-red-400'
            }`}>
              {locationScore.label}
            </span>
            {locationScore.trend_diff != null && locationScore.trend_diff !== 0 && (
              <span className="text-[9px] text-slate-400 dark:text-[#484F58]">
                {locationScore.trend_diff > 0 ? '+' : ''}{locationScore.trend_diff} vs vorige week
              </span>
            )}
          </div>
        )}
      </div>

      {/* Compliance Health Line */}
      <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D] p-4" data-testid="location-health-line">
        <div className="flex items-center gap-6 flex-wrap">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#2E75B6]" />
            <span className="text-sm font-medium text-slate-700 dark:text-[#C9D1D9]">{summary.entry_count} items</span>
          </div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-[#2E75B6]" />
            <span className="text-sm text-slate-600 dark:text-[#8B949E]">{summary.compliance.health_line}</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="location-tabs">
        <div className="overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
          <TabsList className="w-max sm:w-full justify-start">
            <TabsTrigger value="overzicht" className="gap-1.5 text-xs sm:text-sm" data-testid="location-tab-overzicht">
              <MapPin className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Overzicht</span><span className="sm:hidden">Info</span>
            </TabsTrigger>
            <TabsTrigger value="kennisbank" className="gap-1.5 text-xs sm:text-sm" data-testid="location-tab-kennisbank">
              <FileText className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Kennisbank</span><span className="sm:hidden">Items</span>
            </TabsTrigger>
            <TabsTrigger value="compliance" className="gap-1.5 text-xs sm:text-sm" data-testid="location-tab-compliance">
              <ShieldCheck className="w-3.5 h-3.5" /> Compliance
            </TabsTrigger>
            <TabsTrigger value="bestanden" className="gap-1.5 text-xs sm:text-sm" data-testid="location-tab-bestanden">
              <FolderOpen className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Bestanden</span><span className="sm:hidden">Files</span>
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Overzicht Tab */}
        <TabsContent value="overzicht" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Latest Entries */}
            <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D] p-4" data-testid="location-latest-entries">
              <h3 className="text-sm font-heading font-semibold text-slate-900 dark:text-[#E6EDF3] mb-3">Laatste items</h3>
              {summary.latest_entries.length === 0 ? (
                <p className="text-xs text-slate-400 py-4 text-center">Geen items gevonden</p>
              ) : (
                <div className="space-y-1">
                  {summary.latest_entries.map(entry => (
                    <button
                      key={entry.entry_id}
                      onClick={() => navigate(`/kennisbank/${entry.entry_id}`)}
                      className="w-full text-left p-2.5 rounded-lg hover:bg-slate-50 dark:hover:bg-[#1C2128] transition-colors group"
                      data-testid={`location-entry-${entry.entry_id}`}
                    >
                      <p className="text-xs font-medium text-slate-900 dark:text-[#E6EDF3] truncate group-hover:text-[#2E75B6]">{entry.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] text-slate-400">{CATEGORY_LABELS[entry.category] || entry.category}</span>
                        <span className="text-[10px] text-slate-400">{formatDate(entry.updated_at)}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              <Button variant="ghost" size="sm" className="w-full mt-2 text-xs text-[#2E75B6]" onClick={() => setActiveTab('kennisbank')}>
                Alle items bekijken <ChevronRight className="w-3 h-3 ml-1" />
              </Button>
            </div>

            {/* Compliance Summary */}
            <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D] p-4" data-testid="location-compliance-summary">
              <h3 className="text-sm font-heading font-semibold text-slate-900 dark:text-[#E6EDF3] mb-3">Compliance</h3>
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div className="text-center p-2 rounded-lg bg-slate-50 dark:bg-[#0D1117]">
                  <p className="text-lg font-bold tabular-nums text-slate-900 dark:text-[#E6EDF3]">{summary.compliance.total}</p>
                  <p className="text-[10px] text-slate-500">Totaal</p>
                </div>
                <div className={`text-center p-2 rounded-lg ${summary.compliance.overdue > 0 ? 'bg-red-50 dark:bg-red-500/10' : 'bg-slate-50 dark:bg-[#0D1117]'}`}>
                  <p className={`text-lg font-bold tabular-nums ${summary.compliance.overdue > 0 ? 'text-red-600' : 'text-slate-900 dark:text-[#E6EDF3]'}`}>{summary.compliance.overdue}</p>
                  <p className="text-[10px] text-slate-500">Te laat</p>
                </div>
                <div className="text-center p-2 rounded-lg bg-orange-50 dark:bg-orange-500/10">
                  <p className="text-lg font-bold tabular-nums text-orange-600">{summary.compliance.due_soon}</p>
                  <p className="text-[10px] text-slate-500">Binnenkort</p>
                </div>
              </div>

              {/* Next deadlines */}
              {summary.compliance.next_deadlines.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Volgende deadlines</p>
                  {summary.compliance.next_deadlines.map(d => (
                    <div key={d.compliance_id} className="flex items-center justify-between p-2 rounded-lg bg-slate-50 dark:bg-[#0D1117]">
                      <div className="flex items-center gap-2">
                        <Clock className="w-3 h-3 text-slate-400" />
                        <span className="text-xs text-slate-700 dark:text-[#C9D1D9] truncate max-w-[180px]">{d.title}</span>
                      </div>
                      <span className="text-xs tabular-nums text-slate-500">{formatDate(d.due_date)}</span>
                    </div>
                  ))}
                </div>
              )}
              <Button variant="ghost" size="sm" className="w-full mt-2 text-xs text-[#2E75B6]" onClick={() => setActiveTab('compliance')}>
                Alle compliance bekijken <ChevronRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </div>

          {/* Recent Audit Activity */}
          <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D] p-4" data-testid="location-recent-audit">
            <h3 className="text-sm font-heading font-semibold text-slate-900 dark:text-[#E6EDF3] mb-3 flex items-center gap-2">
              <Activity className="w-4 h-4 text-[#2E75B6]" /> Recente activiteit
            </h3>
            {summary.recent_audit.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-4">Geen recente activiteit</p>
            ) : (
              <div className="space-y-1">
                {summary.recent_audit.map((log, i) => (
                  <div key={log.log_id || i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-[#1C2128] transition-colors">
                    <div className="w-6 h-6 rounded-full bg-slate-100 dark:bg-[#21262D] flex items-center justify-center flex-shrink-0">
                      <User className="w-3 h-3 text-slate-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-slate-700 dark:text-[#C9D1D9]">
                        <span className="font-medium">{log.user_name || log.user_id}</span>
                        {' '}{log.action}{' '}
                        <span className="text-slate-500 dark:text-[#8B949E]">{log.entity_title}</span>
                      </p>
                    </div>
                    <span className="text-[10px] text-slate-400 flex-shrink-0 tabular-nums">{formatDate(log.timestamp)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Kennisbank Tab */}
        <TabsContent value="kennisbank" className="mt-4">
          <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D]" data-testid="location-kennisbank-list">
            <div className="px-4 py-3 border-b border-slate-100 dark:border-[#21262D] flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-900 dark:text-[#E6EDF3]">Kennisbank items</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowComplianceForm(true)}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-[#30363D] text-slate-600 dark:text-[#C9D1D9] text-xs font-medium hover:bg-slate-50 dark:hover:bg-[#1C2128] transition-colors"
                  data-testid="location-new-compliance-from-kennisbank"
                >
                  <ShieldCheck className="w-3.5 h-3.5" />
                  Nieuw compliance item
                </button>
                <span className="text-xs text-slate-400 tabular-nums">{entries.length} items</span>
              </div>
            </div>
            {!entriesLoaded ? (
              <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>
            ) : entries.length === 0 ? (
              <div className="p-8 text-center">
                <FileText className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-400">Geen items voor deze locatie</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-[#21262D]">
                {entries.map(entry => (
                  <button
                    key={entry.entry_id}
                    onClick={() => navigate(`/kennisbank/${entry.entry_id}`)}
                    className="w-full text-left px-4 py-3 hover:bg-slate-50 dark:hover:bg-[#1C2128] transition-colors group"
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-slate-900 dark:text-[#E6EDF3] truncate group-hover:text-[#2E75B6]">{entry.title}</p>
                      <ChevronRight className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] text-slate-400">{CATEGORY_LABELS[entry.category] || entry.category || 'Geen categorie'}</span>
                      <span className="text-[10px] text-slate-400">{formatDate(entry.updated_at)}</span>
                      {entry.attachments?.length > 0 && (
                        <span className="text-[10px] text-slate-400 flex items-center gap-0.5">
                          <FolderOpen className="w-2.5 h-2.5" /> {entry.attachments.length}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Compliance Tab */}
        <TabsContent value="compliance" className="mt-4">
          <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D]" data-testid="location-compliance-list">
            <div className="px-4 py-3 border-b border-slate-100 dark:border-[#21262D] flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-900 dark:text-[#E6EDF3]">Compliance items</span>
              <span className="text-xs text-slate-400 tabular-nums">{complianceItems.length} items</span>
            </div>
            {!complianceLoaded ? (
              <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>
            ) : complianceItems.length === 0 ? (
              <div className="p-8 text-center">
                <ShieldCheck className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-400">Geen compliance items voor deze locatie</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-[#21262D]">
                {complianceItems.map(item => {
                  const statusCfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.upcoming;
                  const typeCfg = TYPE_CONFIG[item.type] || { label: item.type, color: 'bg-slate-50 text-slate-700 border-slate-200' };
                  const isSelected = locSelectedIds.has(item.compliance_id);
                  return (
                    <div
                      key={item.compliance_id}
                      className={`flex items-center justify-between px-4 py-3 hover:bg-slate-50 dark:hover:bg-[#1C2128] transition-colors ${isSelected ? 'bg-blue-50/50 dark:bg-blue-500/5' : ''}`}
                      data-testid={`location-compliance-${item.compliance_id}`}
                    >
                      {/* Checkbox */}
                      {item.status !== 'completed' && (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {
                            setLocSelectedIds(prev => {
                              const next = new Set(prev);
                              if (next.has(item.compliance_id)) next.delete(item.compliance_id);
                              else next.add(item.compliance_id);
                              return next;
                            });
                          }}
                          className="w-4 h-4 rounded border-slate-300 dark:border-[#30363D] text-[#2E75B6] focus:ring-[#2E75B6] cursor-pointer mr-3 flex-shrink-0"
                          data-testid={`loc-compliance-checkbox-${item.compliance_id}`}
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 dark:text-[#E6EDF3] truncate">{item.title}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${typeCfg.color}`}>{typeCfg.label}</span>
                          <span className="text-xs text-slate-500">{formatDate(item.due_date)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Badge variant="outline" className={`text-[10px] px-1.5 py-0.5 ${statusCfg.badgeClass}`}>{statusCfg.label}</Badge>
                        {item.status !== 'completed' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-[10px] text-green-600 hover:bg-green-50 dark:hover:bg-green-500/10"
                            onClick={() => setQuickCompleteItem(item)}
                            data-testid={`quick-complete-${item.compliance_id}`}
                          >
                            Afronden
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          {quickCompleteItem && (
            <CompleteComplianceDialog
              open={!!quickCompleteItem}
              onOpenChange={(open) => { if (!open) setQuickCompleteItem(null); }}
              item={quickCompleteItem}
              onSuccess={() => {
                setQuickCompleteItem(null);
                setComplianceLoaded(false);
                loadCompliance();
                fetchSummary();
              }}
            />
          )}

          {/* Bulk Selection Bar */}
          {locSelectedIds.size > 0 && (
            <div className="fixed bottom-4 sm:bottom-6 left-2 right-2 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:w-auto z-50 bg-slate-900 dark:bg-[#21262D] text-white rounded-xl shadow-2xl border border-slate-700 dark:border-[#30363D] px-4 sm:px-6 py-3 flex items-center justify-between sm:justify-start gap-3 sm:gap-4" data-testid="loc-bulk-action-bar">
              <span className="text-sm font-medium tabular-nums">{locSelectedIds.size} geselecteerd</span>
              <div className="w-px h-6 bg-slate-600" />
              <button
                onClick={() => setLocBulkDialogOpen(true)}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-medium transition-colors"
                data-testid="loc-bulk-complete-button"
              >
                <Check className="w-4 h-4" /> Afvinken
              </button>
              <button
                onClick={() => setLocSelectedIds(new Set())}
                className="text-sm text-slate-400 hover:text-white transition-colors"
              >
                Annuleren
              </button>
            </div>
          )}

          {/* Bulk Complete Dialog */}
          {locBulkDialogOpen && (
            <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50" data-testid="loc-bulk-complete-dialog">
              <div className="bg-white dark:bg-[#161B22] rounded-t-xl sm:rounded-xl border border-slate-200 dark:border-[#21262D] shadow-2xl w-full sm:max-w-md sm:mx-4 p-5 sm:p-6">
                <h3 className="font-heading text-base font-semibold text-slate-900 dark:text-[#E6EDF3] mb-1">
                  {locSelectedIds.size} items afronden
                </h3>
                <p className="text-xs text-slate-500 dark:text-[#8B949E] mb-4">
                  Alle geselecteerde items worden als afgerond gemarkeerd.
                </p>
                <div className="mb-4">
                  <label className="text-xs font-medium text-slate-600 dark:text-[#8B949E] block mb-1.5">Notitie (optioneel)</label>
                  <textarea
                    value={locBulkNotes}
                    onChange={(e) => setLocBulkNotes(e.target.value)}
                    className="w-full h-20 rounded-lg border border-slate-200 dark:border-[#30363D] bg-white dark:bg-[#0D1117] text-sm text-slate-900 dark:text-[#E6EDF3] px-3 py-2 resize-none focus:ring-2 focus:ring-[#2E75B6] focus:border-transparent"
                    placeholder="Bijv. 'Maandelijkse controle uitgevoerd'"
                  />
                </div>
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={() => { setLocBulkDialogOpen(false); setLocBulkNotes(''); }}
                    className="px-4 py-2 rounded-lg text-sm text-slate-600 dark:text-[#8B949E] hover:bg-slate-100 dark:hover:bg-[#21262D] transition-colors"
                  >
                    Annuleren
                  </button>
                  <button
                    onClick={handleLocBulkComplete}
                    disabled={locBulkCompleting}
                    className="flex items-center gap-1.5 px-5 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    {locBulkCompleting ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Afronden...</>
                    ) : (
                      <><Check className="w-4 h-4" /> Alles afvinken</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Bestanden Tab */}
        <TabsContent value="bestanden" className="mt-4">
          <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D]" data-testid="location-files-list">
            <div className="px-4 py-3 border-b border-slate-100 dark:border-[#21262D] flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-900 dark:text-[#E6EDF3]">Bestanden</span>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate(`/kennisbank/upload?location=${locationId}`)}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#2E75B6] text-white text-xs font-medium hover:bg-[#27649C] transition-colors"
                  data-testid="location-upload-button"
                >
                  <Upload className="w-3.5 h-3.5" />
                  Upload
                </button>
                <span className="text-xs text-slate-400 tabular-nums">{files.length} bestanden</span>
              </div>
            </div>
            {!filesLoaded ? (
              <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>
            ) : files.length === 0 ? (
              <div className="p-8 text-center">
                <FolderOpen className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-400">Geen bestanden voor deze locatie</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-[#21262D]">
                {files.map(file => (
                  <FileRow key={file.file_id} file={file} onNavigate={() => navigate(`/kennisbank/${file.entry_id}`)} />
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <ComplianceItemForm
        open={showComplianceForm}
        onOpenChange={setShowComplianceForm}
        item={null}
        locations={allLocations}
        users={users}
        onSuccess={() => {
          setShowComplianceForm(false);
          setComplianceLoaded(false);
          fetchSummary();
        }}
      />

      {isOwner && (
        <EditLocationDialog
          open={showEditDialog}
          onOpenChange={setShowEditDialog}
          location={loc}
          authAxios={authAxios}
          onSuccess={fetchSummary}
        />
      )}
    </div>
  );
}

function EditLocationDialog({ open, onOpenChange, location, authAxios, onSuccess }) {
  const [name, setName] = useState(location.name || '');
  const [status, setStatus] = useState(location.status || 'active');
  const [bays, setBays] = useState(location.bays || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(location.name || '');
      setStatus(location.status || 'active');
      setBays(location.bays || '');
    }
  }, [open, location]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Naam is verplicht');
      return;
    }
    setSaving(true);
    try {
      const api = authAxios();
      await api.put(`/locations/${location.location_id}`, {
        name: name.trim(),
        status,
        bays: bays ? parseInt(bays, 10) : null,
      });
      toast.success('Locatie bijgewerkt');
      onOpenChange(false);
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fout bij bijwerken');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]" data-testid="edit-location-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Locatie bewerken</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-[#8B949E] font-medium mb-1 block">Locatie ID</label>
            <p className="text-sm text-slate-400 dark:text-[#484F58] font-mono">{location.location_id}</p>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-[#8B949E] font-medium mb-1 block">Naam</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="edit-location-name"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-[#8B949E] font-medium mb-1 block">Status</label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="edit-location-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Actief</SelectItem>
                <SelectItem value="planned">Gepland</SelectItem>
                <SelectItem value="inactive">Inactief</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-[#8B949E] font-medium mb-1 block">Aantal bays</label>
            <Input
              type="number"
              min="0"
              value={bays}
              onChange={(e) => setBays(e.target.value)}
              placeholder="Bijv. 3"
              data-testid="edit-location-bays"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="edit-location-cancel">
            Annuleren
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#2E75B6] text-white hover:bg-[#27649C]"
            data-testid="edit-location-save"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}
            Opslaan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FileRow({ file, onNavigate }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const { accessTokenRef } = useAuth();

  const handleDownload = (e) => {
    e.stopPropagation();
    const token = accessTokenRef?.current;
    if (!token) return;
    const url = `${API}/api/attachments/${file.file_id}`;
    const a = document.createElement('a');
    a.href = url;
    a.setAttribute('download', file.original_name);
    // Use fetch for auth
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.blob())
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob);
        a.href = blobUrl;
        a.click();
        URL.revokeObjectURL(blobUrl);
      });
  };

  return (
    <div
      className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 dark:hover:bg-[#1C2128] transition-colors cursor-pointer group"
      onClick={onNavigate}
      data-testid={`file-row-${file.file_id}`}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-[#21262D] flex items-center justify-center flex-shrink-0">
          <FileText className="w-4 h-4 text-slate-400" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-900 dark:text-[#E6EDF3] truncate group-hover:text-[#2E75B6]">{file.original_name}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[10px] text-slate-400">{formatFileSize(file.size)}</span>
            <span className="text-[10px] text-slate-400">{formatDate(file.uploaded_at)}</span>
            {file.entry_title && <span className="text-[10px] text-slate-400 truncate max-w-[150px]">{file.entry_title}</span>}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <button
          onClick={handleDownload}
          className="p-1.5 rounded-md text-slate-400 hover:text-[#2E75B6] hover:bg-[#2E75B6]/5 transition-colors"
          title="Download"
          data-testid={`download-file-${file.file_id}`}
        >
          <Download className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onNavigate(); }}
          className="p-1.5 rounded-md text-slate-400 hover:text-[#2E75B6] hover:bg-[#2E75B6]/5 transition-colors"
          title="Naar item"
        >
          <ExternalLink className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
